/**
 * AiModelsDirectoryView — iter-218t (Feb 2026)
 *
 * The "دليل نماذج الذكاء الاصطناعي" screen inside the Cloud GM AI Control Hub.
 *
 * Layout
 *  1. Code-structure explainer at the top so any employee can read a
 *     code like "ASB-AI-GEM-CGB-004" at a glance.
 *  2. Filter bar (platform + provider + status).
 *  3. Data table with columns: Code | Name | Platform | Model Used | Status | Actions.
 *  4. Header action: "+ إضافة نموذج جديد" opens `AiAgentFormDialog` in create mode.
 *  5. Per-row Edit + Deprecate actions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, RefreshCw, BookOpen, Search, LogIn } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import { getAuthHeaders } from '../../utils/authHeaders';
import { AiAgentFormDialog } from './AiAgentFormDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  draft:      { bg: 'bg-white/10',       fg: 'text-white/70',    ar: 'مسودة',    en: 'Draft' },
  active:     { bg: 'bg-emerald-500/15', fg: 'text-emerald-300', ar: 'نشط',      en: 'Active' },
  // iter-232c — "Deprecated" renamed to "متوقف / Stopped" per user request.
  deprecated: { bg: 'bg-rose-500/15',    fg: 'text-rose-300',    ar: 'متوقف',    en: 'Stopped' },
};

export function AiModelsDirectoryView({ language }) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();

  const [platforms, setPlatforms] = useState([]);
  const [providers, setProviders] = useState([]);
  const [agents,    setAgents]    = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [filters, setFilters] = useState({ platform: '', provider: '', status: '', search: '' });
  const [dialogAgentCode, setDialogAgentCode] = useState(null);   // '' = create, string = edit
  const [dialogOpen, setDialogOpen]           = useState(false);

  // ── Load ────────────────────────────────────────────────────────
  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.platform) params.set('platform_code', filters.platform);
      if (filters.provider) params.set('provider_code', filters.provider);
      if (filters.status)   params.set('status',        filters.status);
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/ai-registry?${params.toString()}`,
        { headers: getAuthHeaders() },
      );
      setAgents(r.data?.data || []);
    } catch (e) {
      toast.error(t('فشل تحميل النماذج', 'Failed to load agents'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.platform, filters.provider, filters.status]);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/ai-registry/meta/platforms`, { headers: getAuthHeaders() }),
          axios.get(`${BACKEND_URL}/api/admin/ai-registry/meta/providers`, { headers: getAuthHeaders() }),
        ]);
        setPlatforms(p.data?.data || []);
        setProviders(m.data?.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  // ── In-memory search (over the results returned by the API) ────
  const visibleAgents = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(a =>
      (a.agent_code || '').toLowerCase().includes(q) ||
      (a.name_ar    || '').toLowerCase().includes(q) ||
      (a.name_en    || '').toLowerCase().includes(q),
    );
  }, [agents, filters.search]);

  const platformName = (code) => {
    const p = platforms.find(x => x.code === code);
    return p ? (isAr ? p.name_ar : p.name_en) : code;
  };
  const providerName = (code) => {
    const p = providers.find(x => x.code === code);
    return p ? (isAr ? p.name_ar : p.name_en) : code;
  };

  const openCreate = () => { setDialogAgentCode(null); setDialogOpen(true); };
  const openEdit   = (code) => { setDialogAgentCode(code); setDialogOpen(true); };

  const deprecate = async (agent_code) => {
    if (!window.confirm(t(
      `هل تريد إلغاء النموذج "${agent_code}"؟ لن يُحذف نهائياً — يمكن تفعيله لاحقاً.`,
      `Deprecate agent "${agent_code}"? It is not permanently deleted — you can re-activate later.`,
    ))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/ai-registry/${agent_code}`, { headers: getAuthHeaders() });
      toast.success(t('تم إلغاء النموذج', 'Agent deprecated'));
      loadAgents();
    } catch {
      toast.error(t('فشل الإلغاء', 'Deprecate failed'));
    }
  };

  return (
    <section className="space-y-5" data-testid="ai-models-directory">
      {/* iter-232b — Page title per user: "التحكم بذكاء الاصطناعي" */}
      <div>
        <h2 className="text-2xl font-bold text-white">
          {t('التحكم بذكاء الاصطناعي', 'AI Control')}
        </h2>
        <p className="text-xs text-white/50 mt-1">
          {t('إدارة كل نماذج الذكاء الاصطناعي: الملكية، الاستخدام، الحالة.',
             'Manage every AI model: ownership, usage, status.')}
        </p>
      </div>

      {/* ── Code structure explainer ───────────────────────────── */}
      <div className="rounded-2xl bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 p-5">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-[#d4af37]" />
          <h3 className="font-bold text-white">
            {t('كيف تُقرأ كودات النماذج', 'How to read agent codes')}
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="font-mono text-2xl text-center py-3 rounded-xl bg-black/30 border border-[#d4af37]/20 text-[#d4af37]">
            ASB<span className="text-white/40">-</span>AI<span className="text-white/40">-</span>GEM<span className="text-white/40">-</span>CGB<span className="text-white/40">-</span>001
          </div>
          <ol className="text-[13px] space-y-1.5 text-white/80" dir={isAr ? 'rtl' : 'ltr'}>
            <li><span className="font-mono text-[#d4af37] font-bold">ASB</span> — {t('رمز المشروع الأم (المجموعة كلها)', 'Root project code (parent group)')}</li>
            <li><span className="font-mono text-[#d4af37] font-bold">AI</span>  — {t('مؤشر أن هذا نموذج ذكاء اصطناعي', 'Marks this as an AI agent')}</li>
            <li><span className="font-mono text-[#d4af37] font-bold">GEM</span> — {t('رمز مزود النموذج: GEM = جيميناي · CLA = كلود · OAI = أوبن إيه آي',
                                                                                     'Provider code: GEM = Gemini · CLA = Claude · OAI = OpenAI')}</li>
            <li><span className="font-mono text-[#d4af37] font-bold">CGB</span> — {t('رمز المنصة (ASB / CGB / CGC / …)', 'Platform code (ASB / CGB / CGC / …)')}</li>
            <li><span className="font-mono text-[#d4af37] font-bold">001</span> — {t('الرقم التسلسلي (001 – 999) لكل منصة/مزود',
                                                                                     'Sequence (001 – 999) unique per platform/provider pair')}</li>
          </ol>
        </div>
      </div>

      {/* ── Filters + Add button ───────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40 pointer-events-none" />
          <input
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            placeholder={t('بحث بالكود أو الاسم…', 'Search by code or name…')}
            className="w-full bg-white/[0.03] border border-white/10 rounded-full h-10 ps-10 pe-4 text-sm text-white focus:border-[#d4af37]/40 outline-none"
            data-testid="ai-agents-search"
          />
        </div>
        <select value={filters.platform}
                onChange={(e) => setFilters(f => ({ ...f, platform: e.target.value }))}
                className="bg-white/[0.03] border border-white/10 rounded-full h-10 px-4 text-sm text-white [color-scheme:dark]"
                data-testid="ai-agents-filter-platform">
          <option value="" className="bg-[#0a0a0a] text-white">{t('كل المنصات', 'All platforms')}</option>
          {platforms.filter(p => !p.future).map(p => (
            <option key={p.code} value={p.code} className="bg-[#0a0a0a] text-white">{p.code} — {isAr ? p.name_ar : p.name_en}</option>
          ))}
        </select>
        <select value={filters.provider}
                onChange={(e) => setFilters(f => ({ ...f, provider: e.target.value }))}
                className="bg-white/[0.03] border border-white/10 rounded-full h-10 px-4 text-sm text-white [color-scheme:dark]"
                data-testid="ai-agents-filter-provider">
          <option value="" className="bg-[#0a0a0a] text-white">{t('كل المزودين', 'All providers')}</option>
          {providers.map(p => (
            <option key={p.code} value={p.code} className="bg-[#0a0a0a] text-white">{p.code} — {isAr ? p.name_ar : p.name_en}</option>
          ))}
        </select>
        <select value={filters.status}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                className="bg-white/[0.03] border border-white/10 rounded-full h-10 px-4 text-sm text-white [color-scheme:dark]"
                data-testid="ai-agents-filter-status">
          <option value="" className="bg-[#0a0a0a] text-white">{t('كل الحالات', 'All statuses')}</option>
          <option value="active" className="bg-[#0a0a0a] text-white">{t('نشط', 'Active')}</option>
          <option value="deprecated" className="bg-[#0a0a0a] text-white">{t('متوقف', 'Stopped')}</option>
        </select>
        <Button
          onClick={loadAgents}
          variant="ghost"
          className="text-white/60 hover:bg-white/5 h-10"
          data-testid="ai-agents-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button
          onClick={openCreate}
          className="bg-[#d4af37] hover:bg-[#c9a227] text-black h-10 ms-auto"
          data-testid="ai-agents-add-btn"
        >
          <Plus className="w-4 h-4 me-1.5" />
          {t('إضافة نموذج جديد', 'Add new agent')}
        </Button>
      </div>

      {/* ── Table ─────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" dir={isAr ? 'rtl' : 'ltr'}>
            <thead className="bg-white/[0.03] text-[11px] text-white/50">
              <tr>
                <th className="text-start px-3 py-2.5 font-medium">{t('كود النموذج', 'Agent code')}</th>
                <th className="text-start px-3 py-2.5 font-medium">{t('مسمى النموذج', 'Model name')}</th>
                <th className="text-start px-3 py-2.5 font-medium">{t('المنصة', 'Platform')}</th>
                <th className="text-start px-3 py-2.5 font-medium">{t('النموذج المستخدم', 'Model used')}</th>
                <th className="text-start px-3 py-2.5 font-medium">{t('المميزات', 'Capabilities')}</th>
                {/* iter-232c — Owner-Agent column removed. Bots are now shared
                   across every user of the platform indicated by their code. */}
                <th className="text-start px-3 py-2.5 font-medium">{t('الاستخدام', 'Usage')}</th>
                <th className="text-start px-3 py-2.5 font-medium">{t('الحالة', 'Status')}</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-12 text-center text-white/50">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </td></tr>
              ) : visibleAgents.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-white/50 text-[13px]">
                  {t('لا توجد نماذج بعد — اضغط "إضافة نموذج جديد" لإنشاء أول واحد',
                     'No agents yet — click "Add new agent" to create the first one')}
                </td></tr>
              ) : visibleAgents.map((a) => {
                const s = STATUS_STYLE[a.status] || STATUS_STYLE.draft;
                const caps = (isAr ? a.capabilities_ar : a.capabilities_en) || [];
                return (
                  <tr key={a.agent_code} className="border-t border-white/5 hover:bg-white/[0.02] align-top" data-testid={`ai-agent-row-${a.agent_code}`}>
                    <td className="px-3 py-3 font-mono text-[12px] text-[#d4af37] whitespace-nowrap">{a.agent_code}</td>
                    <td className="px-3 py-3 text-white max-w-[240px]">
                      <div className="font-medium">{isAr ? a.name_ar : a.name_en}</div>
                      {(isAr ? a.description_ar : a.description_en) && (
                        <div className="text-[11px] text-white/40 mt-0.5 line-clamp-2">
                          {isAr ? a.description_ar : a.description_en}
                        </div>
                      )}
                      {/* iter-232b — Legacy code row removed per user request. */}
                    </td>
                    <td className="px-3 py-3 text-white/80 whitespace-nowrap">
                      <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-white/5 me-1.5">{a.platform_code}</span>
                      <span className="text-[12px]">{platformName(a.platform_code)}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-white/80 text-[12px]">
                        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-white/5 me-1.5">{a.provider_code}</span>
                        {a.model_used || providerName(a.provider_code)}
                      </div>
                      {/* iter-299l — Voice I/O badges — ALWAYS visible per user request.
                         🎤 = voice input enabled · 🔊 = voice output enabled · 🧠 = text model.
                         Shown muted when disabled so the admin can tell at a glance. */}
                      <div className="flex items-center gap-1 mt-1.5" data-testid={`ai-agent-voice-badges-${a.agent_code}`}>
                        <span
                          title={(a.voice_input_enabled !== false)
                            ? t('استقبال صوت دخول (Gemini STT)', 'Voice input ON (Gemini STT)')
                            : t('الصوت الدخل متوقف', 'Voice input OFF')}
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            (a.voice_input_enabled !== false)
                              ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                              : 'bg-white/5 text-white/30 border border-white/10'
                          }`}
                        >
                          🎤 {(a.voice_input_enabled !== false)
                            ? t('صوت دخول', 'Voice-In')
                            : t('متوقف', 'off')}
                        </span>
                        <span
                          title={(a.voice_output_enabled !== false)
                            ? t(`صوت رد: ${a.voice_name || 'Puck'} (Gemini TTS)`,
                                `Voice reply: ${a.voice_name || 'Puck'} (Gemini TTS)`)
                            : t('الصوت الخرج متوقف', 'Voice output OFF')}
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            (a.voice_output_enabled !== false)
                              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                              : 'bg-white/5 text-white/30 border border-white/10'
                          }`}
                        >
                          🔊 {(a.voice_output_enabled !== false)
                            ? (a.voice_name || 'Puck')
                            : t('متوقف', 'off')}
                        </span>
                        <span
                          title={t(`الموديل النصّي: ${a.model_used || 'افتراضي'}`,
                                   `Text model: ${a.model_used || 'default'}`)}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30"
                        >
                          🧠 {(a.model_used || '').replace('gemini-', '').replace('-latest', '').replace('-preview', '') || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 max-w-[260px]">
                      {caps.length === 0 ? (
                        <span className="text-[11px] text-white/30">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {caps.slice(0, 4).map((c, i) => (
                            <span key={i} className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-blue-500/10 text-blue-200 border border-blue-500/20 whitespace-nowrap">
                              {c}
                            </span>
                          ))}
                          {caps.length > 4 && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-white/5 text-white/50 border border-white/10">
                              +{caps.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {/* iter-232b — Usage semantics per user:
                         • rate limit disabled → show ONLY the raw counter (no dash, no fake limit).
                         • rate limit enabled → show `used / limit` where limit is whatever
                           admin actually saved (no 500 fallback). */}
                      {(() => {
                        const monthly = a.usage_stats?.monthly_calls ?? 0;
                        if (a.rate_limit_enabled === false) {
                          return (
                            <div className="text-[11px] text-white/70" dir="ltr">
                              {monthly}
                              <span className="text-white/40 ms-1">{t('بلا حد', 'unlimited')}</span>
                            </div>
                          );
                        }
                        if (a.monthly_call_limit) {
                          return (
                            <div className="text-[11px] text-white/70" dir="ltr">
                              {monthly} / {a.monthly_call_limit}
                            </div>
                          );
                        }
                        return <div className="text-[11px] text-white/70" dir="ltr">{monthly}</div>;
                      })()}
                      {a.paused && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] bg-rose-500/15 text-rose-300 border border-rose-500/30">
                          {t('موقوف', 'Paused')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] ${s.bg} ${s.fg}`}>
                        {isAr ? s.ar : s.en}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-end">
                      {/* iter-232b — Single "Enter" button (replaces edit+deprecate).
                         Navigates to a dedicated detail page with edit + stats + pause. */}
                      <button
                        onClick={() => navigate(`/control/ai/models/${encodeURIComponent(a.agent_code)}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/25"
                        data-testid={`ai-agent-open-${a.agent_code}`}
                      >
                        <LogIn className="w-3.5 h-3.5" />
                        {t('فتح', 'Enter')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 border-t border-white/5 text-[11px] text-white/40">
          {t(`الإجمالي: ${visibleAgents.length} نموذج`, `Total: ${visibleAgents.length} agents`)}
        </div>
      </div>

      {dialogOpen && (
        <AiAgentFormDialog
          agentCode={dialogAgentCode}
          onClose={() => setDialogOpen(false)}
          onSaved={loadAgents}
          language={language}
        />
      )}
    </section>
  );
}
